/**
 * Tenant Switcher (Header).
 *
 * Dropdown in the top bar that lists all tenants the current user has access
 * to and switches the active tenant in TenantContext. On switch, the React
 * Query cache is cleared so subsequent fetches target the new tenant.
 *
 * ISS-037 — Sprint 90, Stream C.
 */

import { useTenant } from '@/contexts/TenantContext';
import { useI18n } from '@/i18n';
import type { Tenant } from '@/contexts/TenantContext';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// =============================================================================
// TenantIndicator Component
// =============================================================================

export function TenantIndicator() {
  const { t } = useI18n();
  const { currentTenant, availableTenants, setCurrentTenant, isLoading } = useTenant();
  const queryClient = useQueryClient();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Building2 className="h-4 w-4" />
        <span className="hidden sm:inline">{t('admin.tenantIndicator.loading')}</span>
      </div>
    );
  }

  if (!currentTenant || availableTenants.length === 0) {
    return (
      <Link
        to="/settings"
        className="flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:underline"
      >
        <Building2 className="h-4 w-4" />
        <span className="hidden sm:inline">{t('admin.tenantIndicator.select')}</span>
      </Link>
    );
  }

  const handleSelect = (tenant: Tenant) => {
    if (tenant.id === currentTenant.id) return;
    setCurrentTenant(tenant);
    // Clear caches so next fetches target the new tenant.
    queryClient.clear();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title={t('admin.tenantIndicator.switchTitle')}
      >
        <Building2 className="h-4 w-4" />
        <span className="hidden sm:inline max-w-[160px] truncate">{currentTenant.name}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t('admin.tenantIndicator.workspaces')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableTenants.map((tenant) => {
          const active = tenant.id === currentTenant.id;
          return (
            <DropdownMenuItem
              key={tenant.id}
              onSelect={() => handleSelect(tenant)}
              className="flex items-center justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{tenant.name}</p>
                <p className="truncate text-xs text-muted-foreground">{tenant.slug}</p>
              </div>
              {active && <Check className="h-4 w-4 text-primary shrink-0" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="w-full text-xs text-muted-foreground">
            Manage workspaces…
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
